import _ from 'lodash';
import * as actionTypes from '../../actions/actiontypes';
import initialstate from '../initialstate';
import {HttpStatusCodes, aresTaskViewRoutes} from '../../util/uiconstants';
import Utility from '../../util/utilityfunctions';
import { labels, normalize, getSortedSections, objectComparator } from '../../util/utils';

export function findAresDocument(response) {
	return response.data?.find((document) => document.hasTaskSystemAssociated);
}

export const getDocumentByDocumentId = (state, documentId) => {
	let document;

	document = state.documents.byId[documentId];

	return document;
};

export function processAresTaskDocumentsResponse(response) {
	if (response && response.data) {
		let documentAssociated = findAresDocument(response);

		if (documentAssociated) {
			documentAssociated = {
				...documentAssociated,
				approvals: documentAssociated.approvals
			};
		}

		return documentAssociated;
	}
}

export function findAresTaskId(document) {
	let aresTaskId;

	if (document && !_.isEmpty(document)) {
		aresTaskId = document.tasks?.find((task) => task.isSystemAssociated)?.id;
		if (!aresTaskId) {
			aresTaskId = document.tasks?.[0]?.id;
		}
	}

	return aresTaskId;
}

// Returns a document object with the format expected by the reducer
function mapDocument(responseDocument, taskId) {
	let fitTaskId;

	if (!taskId) {
		fitTaskId = findAresTaskId(responseDocument);
	}

	return {
		...responseDocument,
		taskId: taskId || fitTaskId,
		tasks: responseDocument.tasks?.map((task) => task.id),
		relatedTasks: responseDocument.tasks,
		entities: responseDocument.entities?.map((entitiy) => entitiy.id),
		approvals: responseDocument.approvals?.map((a) => a.id)
	};
}

export function aresTaskDocumentsReducer(state = initialstate.aresTaskDocuments, action) {
	let newState;

	switch (action.type) {
		case actionTypes.GET_ALL_TASKS_OVERVIEW: {
			if (action.responseObj.status === HttpStatusCodes.Ok && action.response) {
				let documentsPayload = [];
				newState = {...state};

				const apiResponse = _.cloneDeep(action.response);

				for (const responseDocument of apiResponse.documents) {
					// Look for the document in the payload. If it exists, update it. If not, add it.
					let payloadDoc = documentsPayload.find((doc) => doc.id === responseDocument.id) || responseDocument;
					payloadDoc.tasks = payloadDoc.tasks || [];
					payloadDoc.relatedTasks = payloadDoc.relatedTasks || [];

					// State document
					const stateDocument = getDocumentByDocumentId(state, responseDocument.id);

					// If the taskId is not in the collection, add it:
					if (!payloadDoc.tasks.includes(responseDocument.taskId)) {
						payloadDoc.tasks = payloadDoc.tasks.concat(responseDocument.taskId);
						const relatedTask = apiResponse.tasks.find((task) => task.id === responseDocument.taskId);
						if (relatedTask) {
							payloadDoc.relatedTasks = payloadDoc.relatedTasks.concat(relatedTask);
						}
					}

					// Form headers and sections:
					if (apiResponse.headers?.length > 0) {
						const documentSections =
							apiResponse.sections?.filter(
								(section) => section.documentId === payloadDoc.id && section.isDisplayed
							) || [];

						// Nested structure, used by createStorybook
						let headersList = apiResponse.headers
							.filter(
								(header) => header.documentId === payloadDoc.id && (header.isCustom || documentSections.length > 0)
							)
							.map((header) => {
								return {
									...header,
									sections: documentSections.filter((section) => section.formHeaderId === header.id)
								};
							});
						headersList = mergeStateAndFormHeadersAndSections(stateDocument?.headers || [], headersList);
						payloadDoc.headers = headersList;
						payloadDoc.headerIds = headersList.map((header) => header.id);

						// "Faking" a form object in order to create the storybook structure
						const formStoryBook = createStoryBook({
							id: payloadDoc.id,
							documentTypeId: payloadDoc.documentTypeId,
							headers: headersList
						});
						payloadDoc.storyBook = normalize(formStoryBook);

						// First header and section:
						const firstStoryBookItem = formStoryBook[0];
						if (firstStoryBookItem) {
							payloadDoc.firstHeaderId = firstStoryBookItem.headerId;
							payloadDoc.firstSectionId = firstStoryBookItem.sectionId;
						}
					}

					documentsPayload.push(payloadDoc);
					newState[responseDocument.taskId] = payloadDoc;
				}

				newState.documents = normalize(documentsPayload, undefined, newState.documents);
				return newState;
			}

			return state;
		}
		case actionTypes.GET_ALL_DOCUMENTS: {
			if (action.responseObj.status === HttpStatusCodes.Ok && action.response?.data && action.isAresDocuments) {
				const documents = action.response.data;
				let documentsPayload = [];

				newState = {...state};

				// Loop through the documents collection
				for (const document of documents) {
					// Get the first system associated task
					const fitTaskId = findAresTaskId(document);

					const mappedDocument = mapDocument(document);
					if (fitTaskId) {
						// Add the document to the state
						newState[fitTaskId] = mappedDocument;
					}

					documentsPayload.push(mappedDocument);
				}

				newState.documents = normalize(documentsPayload, undefined, newState.documents);
				return newState;
			}

			return state;
		}
		case actionTypes.ARES_GET_DOCUMENT_BY_ID: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const responseDocument = action.response;
				if (!responseDocument.id) {
					responseDocument.id = responseDocument.documentId;
				}
				// Get first system associated task from the document:
				const fitTaskId = findAresTaskId(responseDocument);
				const _taskId = action.taskId || fitTaskId;

				// If document exist in state, update response externalStreamId with state value.
				const existingDocument = state.documents.byId[responseDocument.id];
				if (existingDocument) {
					responseDocument.externalStreamId = existingDocument.externalStreamId;
				}

				const payloadDocument = {
					...responseDocument,
					taskId: _taskId,
					tasks: responseDocument.tasks?.map((task) => task.id),
					relatedTasks: responseDocument.tasks,
					entities: responseDocument.entities?.length > 0 
						? responseDocument.entities.map((entity) => entity.id) 
						: existingDocument?.entities || [],
					approvals: responseDocument.approvals?.map((a) => a.id)
				};

				newState = {
					...state,
					[_taskId]: payloadDocument,
					documents: normalize([payloadDocument], undefined, state.documents)
				};
				return newState;
			}

			break;
		}
		case actionTypes.GET_FORM_DATA: {
			let formData = Array.isArray(action.response) ? action.response[0] : action.response;
			if (formData) {
				if (state) {
					newState = {...state};

					let stateForm = getDocumentByDocumentId(newState, formData.id) || {};

					if (stateForm) {
						let storybook = undefined;
						let redirectData = {};
						storybook = createStoryBook(formData);
						// Get the form's first header and section
						redirectData = storybook[0];

						const payloadDocument = {
							...stateForm,
							id: formData.id,
							headers: mergeStateAndFormHeadersAndSections(stateForm.headers, formData.headers, action?.headersSectionReset),
							headerIds: formData.headers?.map((header) => header.id),
							customInstruction: formData.customInstruction,
							documentTypeId: formData.documentTypeId,
							externalStreamId: formData.externalStreamId,
							independenceInformation: formData.independenceInformation,
							knowledgeFormId: formData.knowledgeFormId,
							parentFormEntityId: formData.parentFormEntityId,
							name: formData.name,
							profile: formData.profile,
							stEntityId: formData.stEntityId,
							firstHeaderId: redirectData?.headerId,
							firstSectionId: redirectData?.sectionId,
							storyBook: storybook !== undefined ? normalize(storybook) : {},
							helixAnalyzers: formData.helixAnalyzers,
							embeddedBodyTypeIds: formData?.embeddedBodyTypeIds,
							hasLinkedResponse: formData?.hasLinkedResponse,
							oldestSnapshotId: formData?.oldestSnapshotId,
							smartEvidenceSourceEntityID: formData?.smartEvidenceSourceEntityID,
							originalFirstHeaderId: redirectData?.headerId,
							originalFirstSectionId: redirectData?.sectionId
						};

						if (stateForm.taskId) {
							newState[stateForm.taskId] = payloadDocument;
						}

						newState.documents = normalize([payloadDocument], undefined, newState.documents);
					}
				}
			}
			break;
		}
		case actionTypes.UPDATE_STORYBOOK: {
			if (action.response && Array.isArray(action.response.headers)) {
				const form = getDocumentByDocumentId(state, action.response.id);

				if (form) {
					const storybook = createStoryBook(action.response);
					const payloadDoc = {
						id: form.id,
						firstHeaderId: storybook[0]?.headerId,
						firstSectionId: storybook[0]?.sectionId,
						storyBook: normalize(storybook)
					};

					newState = {
						...state,
						documents: normalize([payloadDoc], undefined, state.documents),
						[form.taskId]: {
							...state[form.taskId],
							...payloadDoc
						}
					};
				}
			}
			break;
		}
		case actionTypes.ARES_UPDATE_TASK_DOCUMENT_ON_SIGN_OFF: {
			newState = _.cloneDeep(state);
			let found = Object.values(newState.documents.byId).find((doc) => doc.id === action.documentId);
			if (found) {
				found.approvals = [...found.approvals, action.signOff.approvalId];
			}

			return newState;
		}
		case actionTypes.ARES_UPDATE_TASK_DOCUMENT_ON_SIGN_OFF_REMOVAL: {
			newState = _.cloneDeep(state);
			let found = Object.values(newState.documents.byId).find((doc) => doc.id === action.documentId);
			if (found) {
				found.approvals = [...found.approvals].filter((so) => so !== action.signOff.approvalId);
			}
			return newState;
		}
		case actionTypes.ARES_UPDATE_TASK_DOCUMENT_ON_CONFIRM_CHANGE_REASON: {
			const {documentChangeTypeId, documentChangeComment, documentExtensionId} = action.document;
			newState = _.cloneDeep(state);
			let found = newState.documents.byId[action.documentId];
			if (found) {
				found.documentChangeTypeId = documentChangeTypeId;
				found.documentChangeComment = documentChangeComment;
				found.documentExtensionId = documentExtensionId;
			}
			return newState;
		}
		case actionTypes.KRONOS_DOCHELPER_UPLOAD_DOCUMENT: {
			let docPayloadInfo = action.meta.docHelper.docPayload;
			if (!docPayloadInfo) {
				return state;
			}
			//entityToUpdate in the below line is defaulted to [0] index element since the docs can be uploaded only to one parent at a time
			let entityName = docPayloadInfo[0]?.parentName;
			let entityToUpdate = state[entityName];

			if (!action.isOnline) {
				action.response.map((item) => {
					let newDoc = docPayloadInfo.find(function(dp) {
						return dp.filePath === item.FileName;
					});
					newDoc.documentId = item.DocumentTransactionId;
					newDoc.documentTypeId = item.documentType;
					newDoc.inProgress = false;
				});
			}

			return state;
		}
		case actionTypes.SET_IS_SHARED: {
			const taskDocument = {...getDocumentByDocumentId(state, action.documentId)};
			taskDocument.isShared = action.isShared;

			return {
				...state,
				documents: normalize([taskDocument], undefined, state.documents),
				[action.taskId.toLowerCase()]: taskDocument
			};
		}
		case actionTypes.UPDATE_DOCUMENT_STATUS: {
			const taskDocument = {...state[action.taskId.toLowerCase()]};
			taskDocument.documentStatusId = action.documentStatusId;

			newState = {
				...state,
				documents: normalize([taskDocument], undefined, state.documents),
				[action.taskId.toLowerCase()]: taskDocument
			};
			break;
		}
		case actionTypes.SET_UNLINK_INITIATED: {
			const taskDocument = {...state[action.taskId.toLowerCase()]};
			taskDocument.isUnlink = action.isUnlink;
			if (taskDocument?.linkedDocuments) {
				taskDocument.linkedDocuments = [];
			}

			return {
				...state,
				documents: normalize([taskDocument], undefined, state.documents),
				[action.taskId.toLowerCase()]: taskDocument
			};
		}
		case actionTypes.ADD_APPROVAL: {
			let stateCopy = _.cloneDeep(state);
			if (action.meta.api.data.isTaskDocumentId) {
				let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
				taskDocument.approvals = taskDocument.approvals
					? taskDocument.approvals.concat(action.response.approvalId)
					: [action.response.approvalId];
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.DELETE_APPROVAL: {
			let stateCopy = _.cloneDeep(state);
			if (action.meta.api.data.isTaskDocumentId) {
				let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
				taskDocument.approvals = taskDocument.approvals
					? taskDocument.approvals.filter((approvalId) => approvalId !== action.approvalId)
					: [];
				taskDocument.approvals = taskDocument.approvals
					? taskDocument.approvals.filter((approvalId) => approvalId !== action.meta.api.data.approvalId)
					: [];
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.DELETE_SECTION: {
			let stateCopy = _.cloneDeep(state);
			let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
			if (taskDocument) {
				let header = taskDocument.headers.filter((header) => header.id === action.meta.api.data.headerId);
				if (header && header.length > 0) {
					header[0].sections = header[0].sections.filter((section) => section.id !== action.meta.api.data.sectionId);
				}
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.DELETE_HEADER: {
			let stateCopy = _.cloneDeep(state);
			let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
			if (taskDocument) {
				taskDocument.headers = taskDocument.headers.filter((header) => header.id !== action.meta.api.data.headerId);
				taskDocument.headerIds = taskDocument.headers.map((header) => header.id);
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.EDIT_HEADER: {
			if (!action.error) {
				let stateCopy = _.cloneDeep(state);
				let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
				if (taskDocument) {
					let header = taskDocument.headers.filter((header) => header.id === action.meta.api.data.headerId);
					if (header && header.length > 0) {
						header[0].name = action.meta.api.data.formHeaderName;
					}
					newState = {
						...stateCopy,
						documents: normalize([taskDocument], undefined, stateCopy.documents)
					};
					return newState;
				}
			}

			return state;
		}
		case actionTypes.CREATE_CUSTOM: {
			let stateCopy = _.cloneDeep(state);
			let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
			if (taskDocument) {
				switch (action.meta.api.data.type) {
					case 'header':
						taskDocument.headers = taskDocument.headers.concat(action.response);
						taskDocument.headerIds = taskDocument.headerIds.concat(action.response.id);
						break;
					case 'section': {
						let header = taskDocument.headers.filter((header) => header.id === action.meta.api.data.headerId);
						if (header && header.length > 0) {
							if (!header[0].sections) {
								header[0].sections = [];
							}
							let newSection = {
								id: action.response.sectionId,
								name: action.response.formSectionName,
								customLabel: action.response.customLabel,
								isCustom: action.response.isCustom
							};
							header[0].sections.push(newSection);
						}
						break;
					}
					default:
						break;
				}
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.CREATE_DUPLICATE_SECTION: {
			let stateCopy = _.cloneDeep(state);
			let taskDocument = stateCopy.documents.byId[action.meta.api.data.documentId.toLowerCase()];
			if (taskDocument) {
				let header = taskDocument.headers.filter((header) => header.id === action.meta.api.data.headerId);
				if (header && header.length > 0) {
					let found = header[0].sections.find((section) => section.id === action.meta.api.data.sectionId);
					if (found) {
						let duplicating = {...found};
						duplicating.id = action.response.sectionId;
						header[0].sections.push(duplicating);
					}
				}
				newState = {
					...stateCopy,
					documents: normalize([taskDocument], undefined, stateCopy.documents)
				};
				return newState;
			}
			return state;
		}
		case actionTypes.GET_LINKED_DOCUMENTS: {
			const taskDocument = getDocumentByDocumentId(state, action.documentId);

			const documentPayload = {
				id: taskDocument.id,
				linkedDocuments: action.response.map((doc) => doc.id)
			};

			newState = {
				...state,
				documents: normalize([documentPayload], undefined, state.documents),
				[action.taskId.toLowerCase()]: {
					...taskDocument,
					...documentPayload
				}
			};

			break;
		}
		case actionTypes.GET_DOCUMENT_SIGN_OFFS: {
			const approvals = action.response;
			if (approvals && approvals.length > 0) {
				const approvalsPayload = [];
				for (let i = 0; i < approvals.length; i++) {
					const approval = approvals[i];
					approvalsPayload.push(approval.approvalId);
				}
				state.documents.byId[approvals[0]?.documentId].approvals = approvalsPayload;
			} else {
				const documentId = action?.meta?.api?.documentId;
				if (documentId) {
					state.documents.byId[documentId].approvals = [];
				}
			}
			return state;
		}
		default:
			return state;
	}

	return newState || state;
}
function getSortedHeaders(formHeaders) {
	const headers = [];

	if (formHeaders && Array.isArray(formHeaders) && formHeaders.length > 0) {
		const sortKey = formHeaders.every(header => header.formHeaderName) ? 'formHeaderName' : 'name';
		const nonCustomHeaders = formHeaders.filter((header) => header.isCustom === false);

			headers.push(...nonCustomHeaders);

			const customHeaders = formHeaders.filter((header) => header.isCustom === true);
			if(customHeaders.length > 0) 
				customHeaders.sort((a, b) => objectComparator(a, b, sortKey));

			headers.push(...customHeaders);
		}

		return headers;
}

// Generates an array with the sequence in which the user can go through the form, from the first header's first section to the last headers' last section
function createStoryBook(formData) {
	// 1. Iterate through displayable headers (isCustom || hasSections with isDisplayed)
	const displayedHeaders = formData.headers?.filter(
		(header) =>
			header.isCustom || (header.sections?.length > 0 && header.sections?.some((section) => section.isDisplayed))
	);

	// For now, Guided workflow V2 documents do not include the summary item. Later on, the summary will be a form body with isSummary = true
	const includeSummary = !Utility.isDocumentTypeFitV2(formData.documentTypeId);

	let storyBook = [];
	if (displayedHeaders && displayedHeaders.length > 0) {
		for (let header of displayedHeaders) {
			let headerSections = getSortedSections(header.sections);
			// 2. Iterate through each header's sections, and add them to the storybook array
			if (headerSections?.length > 0) {
				for (let section of headerSections) {
					if (section.isDisplayed) {
						const directionData = {
							id: header.id + '-' + section.id,
							documentId: formData.id,
							documentTypeId: formData.documentTypeId,
							headerId: header.id,
							sectionId: section.id,
							headerName: header.name || header.formHeaderName,
							sectionName: section.name || section.formSectionName,
							knowledgeFormHeaderId: header.knowledgeFormHeaderId
						};

						storyBook.push(directionData);
					}
				}
			} else {
				!header.isCustom &&
					storyBook.push({
						id: String(header.id),
						documentId: formData.id,
						documentTypeId: formData.documentTypeId,
						headerId: String(header.id),
						headerName: header.name || header.formHeaderName,
						knowledgeFormHeaderId: header.knowledgeFormHeaderId
					});
			}
		}
	}

	if (includeSummary) {
		storyBook.push({
			id: aresTaskViewRoutes.summary,
			documentId: formData.id,
			documentTypeId: formData.documentTypeId,
			headerId: aresTaskViewRoutes.summary,
			headerName: labels.summary
		});
	}

	return storyBook;
}

function mergeStateAndFormHeadersAndSections(stateHeaders, formHeaders, headersSectionReset = false) {
	const stateHeadersMap = new Map(stateHeaders?.map((header) => [header.id, header]));
	const mergedHeaders = [];

	formHeaders?.forEach((formHeader) => {
		const _formHeader = {
			...formHeader,
			knowledgeFormHeaderId: formHeader.knowledgeFormHeaderId || formHeader.knowledgeId,
			sections: formHeader.sections?.map((section) => {
				return {...section, knowledgeFormSectionId: section.knowledgeFormSectionId || section.knowledgeId};
			})
		};

		const stateHeader = stateHeadersMap.get(_formHeader.id);
		if (stateHeader) {
			// Merge header properties, prioritizing formHeader properties
			const mergedHeader = {...stateHeader, ..._formHeader};

			if (stateHeader.sections) {
				// Merge sections
				const stateSectionsMap = new Map(stateHeader.sections.map((section) => [section.id, section]));

				const mergedSections = _formHeader.sections.map((formSection) => {
					let mergedSection = formSection;
					const stateSection = stateSectionsMap.get(formSection.id);

					if (stateSection) {
						// Merge section properties, prioritizing formSection properties
						mergedSection = {...stateSection, ...formSection};
					}
					return {...mergedSection};
				});

				// Add any sections from stateHeader not present in formHeader
				if (!headersSectionReset) {
					stateHeader.sections.forEach((stateSection) => {
						if (!mergedSections.some((mergedSection) => mergedSection.id === stateSection.id)) {
							mergedSections.push(stateSection);
						}
					});
				}

				mergedHeader.sections = getSortedSections(mergedSections);
			}
			mergedHeaders.push(mergedHeader);
		} else {
			_formHeader.sections = getSortedSections(_formHeader.sections);
			mergedHeaders.push(_formHeader);
		}
	});

	// Add any headers from stateHeaders not present in formHeaders
	stateHeaders?.forEach((stateHeader) => {
		if (!mergedHeaders.some((mergedHeader) => mergedHeader.id === stateHeader.id)) {
			mergedHeaders.push(stateHeader);
		}
	});

	return getSortedHeaders(mergedHeaders);
}

export function navigationPanelReducer(state = {}, action) {
	switch (action.type) {
		case actionTypes.SET_LEFT_NAVIGATION_ACTIVE_PANEL:
			return {
				...state,
				activePanelId: action.panelId
			};
		default:
			return state;
	}
}
